import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'
import { api, day, money } from '../api'
import Settings from './Settings'
import { VehicleForm } from './Shop'

/**
 * The holder's profile: what pricing reads about them, what they have earned
 * by referring, and the account's settings. The pricing details come first,
 * and are quick to correct, because circumstances change and a wrong answer is
 * a wrong price.
 */
export default function Profile() {
  return <Settings before={<><PricingDetails /><ReferralPanel /></>} />
}

function PricingDetails() {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const qc = useQueryClient()
  const insured = useQuery({ queryKey: ['insured'], queryFn: api.insured })
  const [licence, setLicence] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const done = () => { message.success(t('saved')); qc.invalidateQueries({ queryKey: ['insured'] }) }
  const saveLicence = useMutation({
    mutationFn: () => api.setLicenceYear(licence ? Number(licence) : null),
    onSuccess: () => { setLicence(null); done() },
    onError: (e: Error) => message.error(e.message),
  })
  const remove = useMutation({
    mutationFn: (id: string) => api.removeVehicle(id),
    onSuccess: done,
    onError: (e: Error) => message.error(e.message),
  })

  const d = insured.data
  return (
    <div className="panel">
      <div className="panel-head"><span className="grow">{t('pricingDetails')}</span></div>
      <div className="panel-body stack" style={{ gap: 12 }}>
        <p className="field-hint">{t('pricingDetailsHint')}</p>
        <dl className="review-grid">
          {d?.birth_date ? (
            <>
              <dt>{t('birthDate')}</dt><dd>{day(d.birth_date)}</dd>
              <dt>{t('age')}</dt><dd>{d.age}</dd>
              <dt>{t('sex')}</dt><dd>{d.sex === 'M' ? t('male') : t('female')}</dd>
            </>
          ) : <><dt>{t('age')}</dt><dd className="muted">{insured.isLoading ? '…' : t('unknownRegNo')}</dd></>}
        </dl>
        <p className="field-hint">{t('fromRegNo')}</p>

        <div className="field">
          <label htmlFor="licence">{t('licenceYear')}</label>
          <div className="row" style={{ gap: 8 }}>
            <div className="input-wrap" style={{ flex: 1 }}>
              <input id="licence" inputMode="numeric" autoComplete="off"
                value={licence ?? String(d?.licence_year ?? '')}
                onChange={e => setLicence(e.target.value.replace(/[^\d]/g, '').slice(0, 4))} />
            </div>
            <button className="btn-small" disabled={licence === null || saveLicence.isPending} onClick={() => saveLicence.mutate()}>
              {t('save')}
            </button>
          </div>
        </div>

        <div className="row">
          <span className="grow" style={{ fontWeight: 600 }}>{t('vehicle')}</span>
          <button className="btn-ghost" onClick={() => setAdding(a => !a)}>{t('addVehicle')}</button>
        </div>
        {adding && <VehicleForm onSaved={() => { setAdding(false); done() }} />}
        {(d?.vehicles.length ?? 0) === 0 && !adding && <p className="empty">{t('noVehicles')}</p>}
        {(d?.vehicles ?? []).map(v => (
          <div className="list-row" key={v.id}>
            <div className="grow">
              <div className="title">{v.plate} · {v.vehicle_class}</div>
              <div className="sub">{[v.make_model, v.year, v.value ? money(v.value) : ''].filter(Boolean).join(' · ')}</div>
            </div>
            <button className="btn-ghost" disabled={remove.isPending} onClick={() => remove.mutate(v.id)}>{t('remove')}</button>
          </div>
        ))}
      </div>
    </div>
  )
}

function ReferralPanel() {
  const { t } = useTranslation()
  const mine = useQuery({ queryKey: ['referrals', 'mine'], queryFn: api.referralsMine })
  const codes = mine.data?.codes ?? []
  // Most people are not agents: an empty referral panel is noise on their page.
  if (!codes.length && !Number(mine.data?.earned ?? 0)) return null
  return (
    <div className="panel">
      <div className="panel-head">
        <span className="grow">{t('myCodes')}</span>
        <span className="chip">{t('earned')} {money(mine.data?.earned)}</span>
      </div>
      <div className="panel-body flush">
        {codes.map(c => (
          <div className="list-row" key={c.code}>
            <div className="grow">
              <div className="title mono">{c.code}</div>
              <div className="sub">{c.label}</div>
            </div>
            {c.disabled_at && <span className="chip">{t('disabledWord')}</span>}
          </div>
        ))}
        <p className="field-hint" style={{ padding: '10px 14px' }}>
          {t('referredSales', { count: mine.data?.policies.length ?? 0 })}
        </p>
      </div>
    </div>
  )
}
